import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import Disclaimer from '../components/Disclaimer';
import { getSavingsByState, ALL_STATE_SLUGS, ALL_STATES } from '../data/savingsByState';

const fmt = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct = n => `${n}%`;

export default function SavingsByState() {
  const { state } = useParams();
  const data = getSavingsByState(state);

  if (!data) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>State not found</h1>
          <p style={s.body}>We have savings data for all 50 US states. Try searching by state name slug (e.g., "california", "texas", "new-york").</p>
          <Link to="/savings-by-state/california" style={s.ctaBtn}>View California →</Link>
        </div>
      </div>
    );
  }

  const colIndex = data.costOfLivingIndex;
  const colStatus = colIndex < 95 ? 'Below Average' : colIndex > 115 ? 'Above Average' : 'Near Average';
  const colColor = colIndex < 95 ? '#22c55e' : colIndex > 115 ? '#ef4444' : '#f59e0b';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `What is the average retirement savings in ${data.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `The average retirement savings for ${data.name} residents is approximately ${fmt(data.avgRetirementSavings)}. The average household income in ${data.name} is ${fmt(data.avgHouseholdIncome)}, and the average savings rate is ${fmtPct(data.avgSavingsRate)}.`,
        },
      },
      {
        '@type': 'Question',
        name: `Is ${data.name} a good state to save money?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${data.name} has a cost of living index of ${data.costOfLivingIndex} (${colStatus} vs. the US average of 100). ${data.context}`,
        },
      },
      {
        '@type': 'Question',
        name: `What is the average credit card debt in ${data.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `The average credit card debt for ${data.name} residents is approximately ${fmt(data.avgCreditCardDebt)}, compared to the national average of about $5,700. Managing and eliminating credit card debt is one of the highest-ROI financial moves available.`,
        },
      },
    ],
  };

  // Get top/bottom 5 states by retirement savings for comparison
  const sortedStates = [...ALL_STATES].sort((a, b) => b.avgRetirementSavings - a.avgRetirementSavings);
  const top5 = sortedStates.slice(0, 5);
  const bottom5 = sortedStates.slice(-5).reverse();
  const currentRank = sortedStates.findIndex(s => s.slug === state) + 1;

  // Nearby states for related links (just a few)
  const nearbyStates = ALL_STATE_SLUGS
    .filter(s => s !== state)
    .slice(0, 6);

  return (
    <div style={s.page}>
      <SEO
        title={`Average Savings in ${data.name} | KnowFi`}
        description={`${data.name} residents save an average of ${fmtPct(data.avgSavingsRate)} of their income, with average retirement savings of ${fmt(data.avgRetirementSavings)}. Cost of living index: ${data.costOfLivingIndex}.`}
        path={`/savings-by-state/${state}`}
        keywords={`average savings in ${data.name}, ${data.name} retirement savings, how much do ${data.name} residents save, ${data.name} personal finance, ${data.name} cost of living savings, average savings rate ${data.name}, ${data.abbr} savings benchmark, average ${data.name} household savings, ${data.name} vs national average savings, ${data.name} savings rate 2024, best places to save money in ${data.name}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>{data.name}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.stateBadge}>{data.abbr} · #{currentRank} of 50</div>
          <h1 style={s.h1}>Average Savings in {data.name}</h1>
          <p style={s.subtitle}>How do {data.name} residents compare on savings, retirement, and debt?</p>
        </div>

        {/* Hero stats */}
        <div style={s.heroGrid}>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Avg Retirement Savings</div>
            <div style={{ ...s.heroStat, color: '#38bdf8' }}>{fmt(data.avgRetirementSavings)}</div>
            <div style={s.heroSub}>#{currentRank} of 50 states</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Avg Household Income</div>
            <div style={{ ...s.heroStat, color: '#a78bfa' }}>{fmt(data.avgHouseholdIncome)}</div>
            <div style={s.heroSub}>per year</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Avg Savings Rate</div>
            <div style={{ ...s.heroStat, color: '#22c55e' }}>{fmtPct(data.avgSavingsRate)}</div>
            <div style={s.heroSub}>of gross income</div>
          </div>
          <div style={s.heroCard}>
            <div style={s.heroLabel}>Cost of Living</div>
            <div style={{ ...s.heroStat, color: colColor }}>{data.costOfLivingIndex}</div>
            <div style={s.heroSub}>{colStatus} (US avg = 100)</div>
          </div>
        </div>

        {/* Context */}
        <div style={s.contextBox}>
          <p style={s.body}>{data.context}</p>
        </div>

        {/* Full data table */}
        <div style={s.sectionTitle}>Financial Profile — {data.name}</div>
        <div style={s.table}>
          <div style={s.tableRow}><div style={s.tableKey}>Average retirement savings</div><div style={s.tableVal}>{fmt(data.avgRetirementSavings)}</div></div>
          <div style={s.tableRow}><div style={s.tableKey}>Average household income</div><div style={s.tableVal}>{fmt(data.avgHouseholdIncome)}</div></div>
          <div style={s.tableRow}><div style={s.tableKey}>Average savings rate</div><div style={s.tableVal}>{fmtPct(data.avgSavingsRate)}</div></div>
          <div style={s.tableRow}><div style={s.tableKey}>Average emergency fund</div><div style={s.tableVal}>{fmt(data.avgEmergencyFund)}</div></div>
          <div style={s.tableRow}><div style={s.tableKey}>Average credit card debt</div><div style={{ ...s.tableVal, color: '#ef4444' }}>{fmt(data.avgCreditCardDebt)}</div></div>
          <div style={{ ...s.tableRow, borderBottom: 'none' }}><div style={s.tableKey}>Cost of living index</div><div style={{ ...s.tableVal, color: colColor }}>{data.costOfLivingIndex} ({colStatus})</div></div>
        </div>

        {/* National comparison */}
        <div style={s.sectionTitle}>How {data.name} Compares to the Nation</div>
        <div style={s.table}>
          {[
            { label: 'Avg retirement savings', state: data.avgRetirementSavings, national: 118000 },
            { label: 'Avg household income', state: data.avgHouseholdIncome, national: 78000 },
            { label: 'Avg credit card debt', state: data.avgCreditCardDebt, national: 5700, lowerIsBetter: true },
            { label: 'Avg emergency fund', state: data.avgEmergencyFund, national: 8500 },
          ].map(({ label, state: sv, national, lowerIsBetter }, i) => {
            const better = lowerIsBetter ? sv <= national : sv >= national;
            return (
              <div key={i} style={{ ...s.tableRow, ...(i === 3 ? { borderBottom: 'none' } : {}) }}>
                <div style={s.tableKey}>{label}</div>
                <div style={s.tableValGroup}>
                  <span style={{ ...s.tableVal, color: better ? '#22c55e' : '#f59e0b' }}>{fmt(sv)}</span>
                  <span style={s.tableNational}>US avg: {fmt(national)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Top/Bottom states */}
        <div style={s.sectionTitle}>States Ranked by Retirement Savings</div>
        <div style={s.table}>
          {top5.map((st, i) => (
            <div key={st.slug} style={{ ...s.tableRow, ...(st.slug === state ? { background: 'rgba(56,189,248,0.06)' } : {}) }}>
              <div style={s.tableKey}>
                <Link to={`/savings-by-state/${st.slug}`} style={{ ...s.tableLink, ...(st.slug === state ? { color: '#38bdf8', fontWeight: '700' } : {}) }}>
                  #{i + 1} {st.name}
                </Link>
                {st.slug === state && <span style={s.youBadge}> ← You</span>}
              </div>
              <div style={s.tableVal}>{fmt(st.avgRetirementSavings)}</div>
            </div>
          ))}
          {currentRank > 5 && currentRank < 46 && (
            <>
              <div style={{ ...s.tableRow, justifyContent: 'center' }}>
                <span style={{ fontSize: '12px', color: '#475569' }}>· · ·</span>
              </div>
              <div style={{ ...s.tableRow, background: 'rgba(56,189,248,0.06)' }}>
                <div style={{ ...s.tableKey, color: '#38bdf8', fontWeight: '700' }}>#{currentRank} {data.name} ← You</div>
                <div style={s.tableVal}>{fmt(data.avgRetirementSavings)}</div>
              </div>
              <div style={{ ...s.tableRow, justifyContent: 'center' }}>
                <span style={{ fontSize: '12px', color: '#475569' }}>· · ·</span>
              </div>
            </>
          )}
          {bottom5.map((st, i) => (
            <div key={st.slug} style={{ ...s.tableRow, ...(i === 4 ? { borderBottom: 'none' } : {}), ...(st.slug === state ? { background: 'rgba(56,189,248,0.06)' } : {}) }}>
              <div style={s.tableKey}>
                <Link to={`/savings-by-state/${st.slug}`} style={{ ...s.tableLink, ...(st.slug === state ? { color: '#38bdf8', fontWeight: '700' } : {}) }}>
                  #{50 - 4 + i} {st.name}
                </Link>
                {st.slug === state && <span style={s.youBadge}> ← You</span>}
              </div>
              <div style={s.tableVal}>{fmt(st.avgRetirementSavings)}</div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>📊 Build your savings plan no matter where you live</div>
          <div style={s.ctaRow}>
            <Link to="/tracker" style={s.ctaBtn}>📋 Track My Savings</Link>
            <Link to="/gameplan" style={s.ctaBtnSecondary}>🗺️ Get My GamePlan</Link>
          </div>
        </div>

        {/* Related states */}
        <div style={s.sectionTitle}>View Other States</div>
        <div style={s.relatedGrid}>
          {nearbyStates.map(slug => {
            const st = getSavingsByState(slug);
            if (!st) return null;
            return (
              <Link key={slug} to={`/savings-by-state/${slug}`} style={s.relatedCard}>
                <div style={s.relatedLabel}>{st.abbr} — {st.name}</div>
                <div style={s.relatedStat}>{fmt(st.avgRetirementSavings)}</div>
                <div style={s.relatedSub}>avg retirement savings</div>
              </Link>
            );
          })}
        </div>

        <div style={s.exploreRow}>
          <div style={s.exploreLabel}>Explore More Data</div>
          <div style={s.exploreLinks}>
            <Link to="/savings-by-age/30" style={s.exploreLink}>📈 Savings by Age</Link>
            <Link to="/contribution-limits/roth-ira/2024" style={s.exploreLink}>💰 Contribution Limits</Link>
            <Link to="/market-returns/sp500/2024" style={s.exploreLink}>📊 Market Returns</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px', flexWrap: 'wrap' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:    { marginBottom: '32px' },
  stateBadge:{ display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:        { fontSize: 'clamp(22px, 4vw, 38px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle:  { fontSize: '16px', color: '#64748b', margin: 0, lineHeight: 1.65 },

  heroGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '24px' },
  heroCard: { background: '#1e293b', borderRadius: '12px', padding: '20px', border: '1px solid #334155', textAlign: 'center' },
  heroLabel:{ fontSize: '11px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontWeight: '600' },
  heroStat: { fontSize: 'clamp(20px, 3.5vw, 28px)', fontWeight: '900', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: '6px' },
  heroSub:  { fontSize: '12px', color: '#475569' },

  contextBox:{ background: '#1e293b', borderRadius: '12px', padding: '18px 22px', marginBottom: '28px', border: '1px solid #334155' },
  body:      { fontSize: '15px', color: '#94a3b8', lineHeight: 1.75, margin: 0 },

  sectionTitle: { fontSize: '13px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', marginTop: '28px' },

  table:        { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden', marginBottom: '8px' },
  tableRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid #334155', gap: '12px' },
  tableKey:     { fontSize: '13px', color: '#64748b', flex: 1 },
  tableVal:     { fontSize: '14px', fontWeight: '700', color: '#f1f5f9', textAlign: 'right' },
  tableValGroup:{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' },
  tableNational:{ fontSize: '11px', color: '#475569' },
  tableLink:    { color: '#64748b', textDecoration: 'none' },
  youBadge:     { color: '#38bdf8', fontWeight: '700', fontSize: '12px' },

  exploreRow:      { marginBottom: '28px', marginTop: '28px' },
  exploreLabel:    { fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  exploreLinks:    { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  exploreLink:     { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', fontSize: '13px', fontWeight: '600', padding: '8px 14px', borderRadius: '8px', textDecoration: 'none' },
  ctaSection:      { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '14px', padding: '24px', marginTop: '32px', marginBottom: '28px', textAlign: 'center' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },

  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '28px' },
  relatedCard: { background: '#1e293b', borderRadius: '10px', padding: '14px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc' },
  relatedLabel:{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '4px' },
  relatedStat: { fontSize: '18px', fontWeight: '900', color: '#38bdf8', marginBottom: '2px' },
  relatedSub:  { fontSize: '11px', color: '#475569' },
};
